import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Loader2, X, AlertCircle, CheckCircle2, TrendingUp } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScoreBreakdownModal({ open, onClose, lead }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !lead) return;
    setLoading(true); setError(null); setData(null);
    // Explicit flag set by callers when a lead is known to exist in MongoDB.
    // Defaults to false (use POST with body) — safer than guessing from sentinel fields.
    const isPersisted = lead._persisted === true;
    const req = isPersisted
      ? axios.get(`${API}/score-breakdown/${lead.id}?lang=${lang}`)
      : axios.post(`${API}/score-breakdown`, { lead, lang });
    req.then(({ data }) => setData(data))
       .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed"))
       .finally(() => setLoading(false));
  }, [open, lead, lang]);

  if (!open) return null;
  const badgeBg = (b) => b === "QUENTE" ? "bg-[#FEE2E2] text-[#DC2626]" :
                          b === "FRIO"   ? "bg-[#DCFCE7] text-[#16A34A]" :
                                            "bg-[#FFEDD5] text-[#C2410C]";

  return (
    <div data-testid="score-breakdown-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="al-card max-w-[680px] w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#3B82F6]" />
            <h2 className="text-[16px] font-bold text-[#111827]">{t("score.modal_title")}</h2>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]" data-testid="score-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" />
          </div>
        )}
        {error && <p className="text-[13px] text-[#DC2626] py-6 text-center">{error}</p>}

        {data && (
          <div data-testid="score-breakdown-content">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[13px] text-[#6B7280]">{data.name}</div>
                <div className="text-[28px] font-bold text-[#111827] leading-none mt-1">
                  {data.digital_score}<span className="text-[14px] text-[#9CA3AF] font-normal">/{data.max_score}</span>
                </div>
              </div>
              <span data-testid="score-badge" className={`px-3 py-1 rounded-full text-[13px] font-bold ${badgeBg(data.badge)}`}>
                {data.badge}
              </span>
            </div>

            <p className="text-[11.5px] text-[#6B7280] mb-3 italic">
              {lang === "pt" ? data.interpretation_pt : data.interpretation_en}
            </p>

            <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#6B7280] mb-2">{t("score.criteria_title")}</h3>
            <div className="space-y-1.5 mb-5" data-testid="score-criteria-list">
              {data.criteria.map((c) => (
                <div key={c.key} data-testid={`score-criterion-${c.key}`} className="flex items-center gap-2 text-[12.5px]">
                  {c.hit ? (
                    <CheckCircle2 className="w-4 h-4 text-[#16A34A] shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#DC2626] shrink-0" />
                  )}
                  <span className={c.hit ? "text-[#111827] font-medium" : "text-[#6B7280]"}>{c.label}</span>
                  <span className="ml-auto tabular-nums text-[11px] text-[#9CA3AF]">+{c.contribution}/{c.weight}</span>
                </div>
              ))}
            </div>

            {data.opportunities.length > 0 && (
              <>
                <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#6B7280] mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-[#F59E0B]" /> {t("score.opportunities_title")}
                </h3>
                <ul className="space-y-1.5" data-testid="score-opportunities-list">
                  {data.opportunities.map((o) => (
                    <li key={o.key} data-testid={`score-opportunity-${o.key}`}
                        className="text-[12px] text-[#374151] bg-[#FEF3C7] rounded-md px-2.5 py-1.5 border-l-2 border-[#F59E0B]">
                      <strong className="text-[#111827]">{o.label}</strong> · <span className="text-[#6B7280]">{o.opportunity}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
